import { Breadcrumb, Input, Select } from 'antd'
import { Edit2 } from 'iconsax-react'
import { AdminsGetProductListResponse } from 'generated/api'
import { IconSearch } from 'components/icons'
import Card from 'components/card/Card'
import Button from 'components/button/Button'
import Errors from 'components/errors/Errors'
import Pagination from 'components/pagination/Pagination'
import ItemRender from 'components/breadcrumb/ItemRender'
import {
  DropDownEcommerceProductStatus,
  DropDownEcommerceProductStatusEnum,
} from 'constants/dropdown'
import { productItems } from 'constants/breadcrumb/items'
import { mapStatusProduct } from 'helpers/wording'

interface IProps {
  statusFetch: 'loading' | 'failed' | 'idle'
  products: AdminsGetProductListResponse[]
  productStatus: DropDownEcommerceProductStatusEnum
  searchKeyword: string
  totalRows: number
  currentPage: number
  limit: number
  onChangeSearchKeyword: (keyword: string) => void
  onEnterSearchKeyword: () => void
  onSelectProductStatus: (status: DropDownEcommerceProductStatusEnum) => void
  onChangeLimit: (limit: number) => void
  onChangePage: (page: number) => void
  onFetch: () => void
  onOpenCreate: () => void
  onOpenEdit: (product: AdminsGetProductListResponse) => void
}

interface PropsTable {
  products: AdminsGetProductListResponse[]
  onOpenEdit: (product: AdminsGetProductListResponse) => void
}

export default function ProductTable(props: IProps) {
  const loading = props.statusFetch === 'loading'
  const failed = props.statusFetch === 'failed'
  const idle = props.statusFetch === 'idle'

  return (
    <Card
      loading={loading}
      dataTestId="card_products_list"
      spinnerTestId="spinner_products_list"
      className="rounded-xl p-0 pb-2"
    >
      <Card.Header className="rounded-t-xl block xl:flex py-2 px-4 sticky-card-header">
        <div className="xl:w-2/5 w-full flex">
          <Breadcrumb
            className="self-center"
            itemRender={route => ItemRender(route)}
            items={productItems}
            separator=">"
          />
        </div>
        <div className="xl:w-3/5 w-full">
          <div className="xl:flex xl:flex-row xl:gap-4">
            <div className="xl:w-3/5 mb-4 xl:mb-0">
              <Input
                prefix={<IconSearch />}
                className="input-search"
                data-testid="input_search_product"
                placeholder="Masukan Nama Produk"
                style={{ height: 40 }}
                value={props.searchKeyword}
                onChange={({ target: { value } }) => props.onChangeSearchKeyword(value)}
                onPressEnter={props.onEnterSearchKeyword}
              />
            </div>
            <div className="xl:w-1/5 mb-4 xl:mb-0">
              <Select
                data-testid="select_product_status"
                value={props.productStatus}
                className="dropdown-search"
                onChange={(productStatus: DropDownEcommerceProductStatusEnum) =>
                  props.onSelectProductStatus(productStatus)
                }
              >
                {DropDownEcommerceProductStatus.map(status => {
                  return (
                    <Select.Option
                      data-testid={`select_product_status_${status.value}`}
                      key={status.value}
                      value={status.value}
                    >
                      {status.label}
                    </Select.Option>
                  )
                })}
              </Select>
            </div>
            <div className="xl:w-1/5 mb-4 xl:mb-0">
              <Button
                data-testid="button_add_product"
                variant="solid-primary"
                block={true}
                label="Tambah"
                className="text-sm h-full"
                onClick={props.onOpenCreate}
              />
            </div>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="mt-0">
        {failed && (
          <Errors
            dataTestId="products"
            inverse={false}
            header="Mohon maaf, permintaan Anda tidak dapat diproses."
            paragraph="terjadi kesalahan internal pada server kami. Silakan coba beberapa saat lagi."
            button={true}
            buttonLabel="Coba lagi"
            buttonAction={props.onFetch}
          />
        )}
        {loading ? <LoadingContent /> : null}
        {idle && props.products.length < 1 ? <NotFound /> : null}
        {idle && props.products.length >= 1 ? (
          <Table onOpenEdit={props.onOpenEdit} products={props.products} />
        ) : null}
      </Card.Body>
      <Card.Footer>
        <div className="px-4 py-3">
          <Pagination
            setLimit={(limit: number) => props.onChangeLimit(limit)}
            onPageChanged={(page: number) => props.onChangePage(page)}
            totalRows={props.totalRows}
            currentPage={props.currentPage}
            limit={props.limit}
            showLimitPage={true}
          />
        </div>
      </Card.Footer>
    </Card>
  )
}

function Table(props: PropsTable) {
  const { products, onOpenEdit } = props

  return (
    <div data-testid="table_product">
      {products.map(product => (
        <div key={product.id} className="border-y border-x-0 border-[#E5E5E7] border-solid">
          <div className="md:flex md:flex-row gap-8 px-4 py-2">
            <div>
              <img
                className="w-32 h-32 object-contain"
                src={product.imageURL}
                alt={product.imageURL}
              />
            </div>
            <div className="md:flex md:flex-col md:justify-around w-full">
              <div className="block md:flex md:flex-row md:gap-2">
                <div className="md:w-4/5 mb-4 md:mb-0">
                  <p className="text-xl font-semibold my-0">{product.name}</p>
                </div>
                <div className="md:w-1/5 md:flex md:justify-end">
                  <Button
                    data-testid="button-edit"
                    icon={<Edit2 size="16" color="#3C5337" variant="Bold" />}
                    className="rounded-xl border"
                    variant="outline-primary"
                    label="Edit"
                    onClick={() => onOpenEdit(product)}
                  />
                </div>
              </div>
              <div className="md:flex md:justify-between md:w-4/5 block mt-4 w-full md:gap-4 text-base">
                <div className="mb-2 md:mb-2 md:w-1/4">
                  <p className="mt-0 mb-2 text-[#596574] font-normal">Tipe</p>
                  <p className="my-0 font-medium">{product.type}</p>
                </div>
                <div className="mb-2 md:mb-2 md:w-1/4">
                  <p className="mt-0 mb-2 text-[#596574] font-normal">Kategori</p>
                  <p className="my-0 font-medium">{product.category}</p>
                </div>
                <div className="mb-2 md:mb-2 md:w-1/4">
                  <p className="mt-0 mb-2 text-[#596574] font-normal">SKU</p>
                  <p className="my-0 font-medium">{product.sku}</p>
                </div>
                <div className="mb-2 md:mb-2 md:w-1/4">
                  <p className="mt-0 mb-2 text-[#596574] font-normal">Status</p>
                  <p className="my-0 font-medium">{mapStatusProduct(product.status)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function NotFound() {
  return (
    <div className="flex flex-col items-center py-20" data-testid="notfound_product">
      <img src="/media/socialSearch.svg" alt="iconSearch" />
      <h4 className="text-xl mt-5 text-[#858C95]">Belum Ada Produk</h4>
    </div>
  )
}

function LoadingContent() {
  return <div className="flex flex-col items-center py-20"></div>
}
